import {
  WiCloud,
  WiDayCloudy,
  WiDaySunny,
  WiDust,
  WiFog,
  WiRain,
  WiRaindrops,
  WiSnow,
  WiThunderstorm,
} from "react-icons/wi";

import { DATE_TIME_CONFIG } from "../../../config";

/**
 * Format an OpenWeather numeric value for a compact details row.
 *
 * @param {number|null|undefined} value Weather value.
 * @param {string} suffix Unit suffix.
 * @returns {string}
 */
export function formatWeatherValue(value, suffix = "") {
  if (value == null || Number.isNaN(Number(value))) {
    return "--";
  }
  return `${Math.round(Number(value))}${suffix}`;
}

/**
 * Format precipitation without rounding trace amounts down to zero.
 *
 * @param {number|null|undefined} value Precipitation value from OpenWeather.
 * @returns {string}
 */
export function formatPrecipitation(value) {
  if (value == null || Number.isNaN(Number(value))) {
    return "--";
  }
  const amount = Number(value);
  return `${amount < 1 ? amount.toFixed(2) : amount.toFixed(1)} mm/h`;
}

/**
 * Format an OpenWeather Fahrenheit temperature for compact display.
 *
 * @param {number|null|undefined} temp Temperature value from OpenWeather.
 * @returns {string}
 */
export function formatTemperature(temp) {
  if (temp == null || Number.isNaN(Number(temp))) {
    return "--";
  }
  return `${Math.round(Number(temp))}°F`;
}

/**
 * Format a Unix timestamp as Pacific Time.
 *
 * @param {number|null|undefined} timestamp Unix timestamp in seconds.
 * @returns {string}
 */
export function formatPacificTime(timestamp) {
  if (timestamp == null || Number.isNaN(Number(timestamp))) {
    return "--";
  }
  return new Intl.DateTimeFormat("en-US", {
    hour: "numeric",
    minute: "2-digit",
    timeZone: DATE_TIME_CONFIG.TIMEZONE,
  }).format(new Date(Number(timestamp) * 1000));
}

/**
 * Build a readable condition label when OpenWeather omits the `weather` array.
 *
 * @param {Object} dailyWeather OpenWeather daily row.
 * @returns {string}
 */
export function getWeatherConditionLabel(dailyWeather) {
  const explicitCondition = dailyWeather.weather?.[0]?.description;
  if (explicitCondition) {
    return explicitCondition;
  }
  const rain = Number(dailyWeather.rain?.["1h"] ?? dailyWeather.rain ?? 0);
  const snow = Number(dailyWeather.snow?.["1h"] ?? dailyWeather.snow ?? 0);
  const clouds = Number(dailyWeather.clouds ?? 0);
  if (snow > 0) {
    return "snow";
  }
  if (rain > 0) {
    return "rain";
  }
  if (clouds >= 60) {
    return "cloudy";
  }
  if (clouds >= 20) {
    return "partly cloudy";
  }
  return "clear";
}

/**
 * Select a Weather Icons glyph for an OpenWeather daily row.
 *
 * @param {Object} condition OpenWeather `weather[0]` value.
 * @param {Object|null} dailyWeather OpenWeather daily timeline row.
 * @returns {import('react').ComponentType<{ className?: string }>}
 */
export function getWeatherIcon(condition, dailyWeather) {
  const normalized = String(
    condition?.main || condition?.description || ""
  ).toLowerCase();
  const conditionId = Number(condition?.id);
  const iconCode = String(condition?.icon || "");
  const rainAmount = Number(dailyWeather?.rain?.["1h"] ?? dailyWeather?.rain ?? 0);
  const snowAmount = Number(dailyWeather?.snow?.["1h"] ?? dailyWeather?.snow ?? 0);
  const cloudPercent = Number(dailyWeather?.clouds ?? 0);

  if (conditionId >= 200 && conditionId < 300) {
    return WiThunderstorm;
  }
  if (conditionId >= 300 && conditionId < 400) {
    return WiRaindrops;
  }
  if (conditionId >= 500 && conditionId < 600) {
    return WiRain;
  }
  if (conditionId >= 600 && conditionId < 700) {
    return WiSnow;
  }
  if (conditionId >= 700 && conditionId < 800) {
    return WiFog;
  }
  if (iconCode.startsWith("03") || iconCode.startsWith("04")) {
    return WiCloud;
  }
  if (iconCode.startsWith("09") || iconCode.startsWith("10")) {
    return WiRain;
  }
  if (iconCode.startsWith("11")) {
    return WiThunderstorm;
  }
  if (iconCode.startsWith("13")) {
    return WiSnow;
  }
  if (iconCode.startsWith("50")) {
    return WiFog;
  }
  if (normalized === "thunderstorm") {
    return WiThunderstorm;
  }
  if (normalized === "drizzle") {
    return WiRaindrops;
  }
  if (normalized === "rain") {
    return WiRain;
  }
  if (normalized === "snow") {
    return WiSnow;
  }
  if (["mist", "smoke", "haze", "fog"].includes(normalized)) {
    return WiFog;
  }
  if (["sand", "dust", "ash", "squall", "tornado"].includes(normalized)) {
    return WiDust;
  }
  if (normalized === "clouds") {
    return WiCloud;
  }
  if (rainAmount > 0) {
    return WiRain;
  }
  if (snowAmount > 0) {
    return WiSnow;
  }
  if (cloudPercent >= 60) {
    return WiCloud;
  }
  if (cloudPercent >= 20) {
    return WiDayCloudy;
  }
  return WiDaySunny;
}

export { WiCloud };
